/**
 * AuthLogic - Service functions for authentication
 * Object with pure functions; getAuthService returns cached AuthService
 */

import type { AuthService, JsonWebToken } from './types';
import { JwtPayloadSchema } from './schema';
import { PWAAuthService } from './pwa_auth_service';
import { NativeAuthService } from './native_auth_service';

// Extend Window to include Capacitor API
interface WindowWithCapacitor extends Window {
  Capacitor?: {
    isNativePlatform(): boolean;
  };
}

/**
 * Check if running in a native environment (pure).
 */
function isNativeEnvironment(): boolean {
  const win =
    typeof window !== 'undefined'
      ? (window as unknown as WindowWithCapacitor)
      : null;
  return Boolean(
    win &&
      win.Capacitor !== undefined &&
      win.Capacitor.isNativePlatform() === true
  );
}

/** Cached AuthService instance (private to module) */
let cachedAuthService: AuthService | null = null;

export const AuthLogic = {
  /**
   * Get the appropriate authentication service for the current platform.
   * Returns cached instance; creates and caches on first call.
   */
  getAuthService(): AuthService {
    if (!cachedAuthService) {
      if (isNativeEnvironment()) {
        cachedAuthService = new NativeAuthService();
      } else {
        cachedAuthService = new PWAAuthService();
      }
    }
    return cachedAuthService;
  },
};

// --- JsonWebToken logic ---

const EMPTY_JWT: JsonWebToken = {};

function decodeBase64Payload(token: string): unknown {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const payload = parts[1];
  const decoded = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = decoded + '='.repeat((4 - (decoded.length % 4)) % 4);
  const jsonPayload = atob(padded);
  return JSON.parse(jsonPayload) as unknown;
}

export const JWTLogic = {
  /**
   * Decode JWT token to a plain object.
   * Does not verify the signature (safe for client-side expiration checks).
   * Returns empty JsonWebToken on decode/parse failure.
   */
  decodeJwt(token: string): JsonWebToken {
    try {
      const raw = decodeBase64Payload(token);
      if (raw === null) return EMPTY_JWT;

      const result = JwtPayloadSchema.safeParse(raw);
      if (!result.success) {
        // eslint-disable-next-line no-console
        console.error('Failed to parse JWT payload:', result.error);
        return EMPTY_JWT;
      }

      const p = result.data;
      return {
        sub: p.sub,
        privateClaims: {
          phoneVerifExp: p.phoneVerifExp,
          emailVerifExp: p.emailVerifExp,
          migrationProofExp: p.migrationProofExp,
        },
        exp: p.exp,
        iat: p.iat,
        nbf: p.nbf,
        iss: p.iss,
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to decode JWT token:', error);
      return EMPTY_JWT;
    }
  },

  /**
   * Get expiration time from JWT in milliseconds since epoch.
   */
  getExpiration(jwt: JsonWebToken): number | undefined {
    if (jwt.exp == null) return undefined;
    return jwt.exp * 1000;
  },

  /**
   * Check if JWT is expired.
   * Returns false when the JWT has no exp claim (token never expires).
   */
  isExpired(jwt: JsonWebToken): boolean {
    const expiration = JWTLogic.getExpiration(jwt);
    if (expiration == null) return false;
    return Date.now() >= expiration;
  },

  /**
   * Get time until expiration in milliseconds.
   * Returns negative value if already expired.
   */
  getTimeUntilExpiration(jwt: JsonWebToken): number | undefined {
    const expiration = JWTLogic.getExpiration(jwt);
    if (expiration == null) return undefined;
    return expiration - Date.now();
  },

  /**
   * Extracts the JWT token from a JSON response (if present) and stores it via
   * authService.setToken(). Only call for mutation responses (POST/PUT/PATCH/DELETE)
   * because GET responses echo back the request token, and a stale GET response
   * arriving after a fresh mutation would overwrite the newer token.
   */
  async applyTokenFromResponse(
    response: Response,
    authService: AuthService
  ): Promise<void> {
    if (!response.ok) return;
    const ct = response.headers.get('Content-Type') ?? '';
    if (!ct.includes('application/json')) return;
    try {
      const data = await response.clone().json();
      if (data?.token) {
        const jwt = JWTLogic.decodeJwt(data.token);
        await authService.setToken({
          token: data.token,
          expiresAt: JWTLogic.getExpiration(jwt) ?? undefined,
        });
      }
    } catch {
      // Non-JSON body or missing token — ignore
    }
  },
};
